<template>
  <div class="c-container">
    <form ref="form" class="c-form" @submit.prevent="submit.exec">
      <div v-if="submit.err" class="c-message c-message--error u-mbm">
        <p>{{ t('updateProfileError') }}</p>
      </div>
      <div class="c-message c-message--error u-mbm u-hide add-link-error">
        <p>{{ t('addLinkError') }}</p>
      </div>
      <div class="c-form__field c-field">
        <div id="profile-links" ref="profileLinksElement" class="c-field__wrapper">
          <ul v-if="links.length >= 0" class="c-profile__items">
            <li v-for="(link, index) in links" :key="index" :link-id="link.id" :link-type="link.linkType" class="c-profile__item">
              <div class="c-profile__link-content">
                <a :href="link.url" class="c-profile__link u-show" target="_blank">
                  <span class="fa-li"><span class="fa-xl" /></span>
                  <span class="c-profile__link-text">{{ link.text }}</span>
                </a>
              </div>
              <div class="c-profile__link-edit u-hide">
                <span class="u-flex">
                  <span class="fa-li c-profile__trash-icon"><span class="fa-xl fa-solid fa-trash-can" /></span>
                  <textarea
                    v-model.trim="link.url"
                    :index="linkIndex"
                    class="c-field__input c-profile__link-textarea"
                    cols="30"
                    rows="10"
                    @input="onTextareaInput"
                  />
                </span>
              </div>
            </li>
            <li class="c-profile__link-add-item u-mts u-hide">
              <div class="c-profile__link-content">
                <span class="fa-li"><span class="fa-xl fa-solid fa-circle-plus" /></span>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <!-- submit -->
      <fieldset class="c-form__actions u-hide">
        <BtnSubmit id="button-submit-links" ref="buttonSubmit" :loading="submit.loading" :label="t('updateProfile')" class="c-profile__button-submit" />
      </fieldset>
    </form>
  </div>
  <p v-if="links.length === 0" class="links-no-text">{{ t('linksPlaceholder') }}</p>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from '@/utils/i18n'
import { ProfileLink, getProfileLinks, postProfileLink, updateProfileLink, deleteProfileLink, updateDateModified, useRequest } from '@/utils/api'
import { formatDateTime } from '@/utils/format-date'

import BtnSubmit from '@/components/buttons/BtnSubmit.vue'

const props = defineProps<{
  userId: string,
  userName: string,
  profileId: number,
  profileName: string,
  links: any
}>()

let userId: number
let userName: string
let profileId: number
let profileName: string

let isSameUser: boolean

let isEditable = false
let isEdited = false

interface Link {
  id: number,
  url: string,
  text: string|undefined,
  linkType: number,
  linkElement: Element
  inputElement: Element
}

let links: Link[] = []
links = []
const linksUrls = ref(parseLinks(props.links))

let userLinks: ProfileLink[]
const deletedLinks: Link[] = []

let linkIndex: number = 0

const form = ref<HTMLFormElement>()

const profileLinksElement = ref()

const editIcon = <HTMLElement>document.querySelector('.links-edit__icon')
const cancelIcon = <HTMLElement>document.querySelector('.links-cancel__icon')
const confirmIcon = <HTMLElement>document.querySelector('.links-confirm__icon')

let addLinkElement: Element
let deleteLinkElements: NodeList

let buttonSubmit: HTMLButtonElement

const { t } = useI18n({
  messages: {
    en: {
      name: 'Name',
      required: 'required',
      tags: 'Tags',
      linksPlaceholder: "This user hasn't added any links yet.",
      updateProfile: 'Update',
      updateProfileError: 'Error while updating profile.',
      addLinkError: 'Please enter a valid url.'
    },
    fr: {
      name: 'Nom',
      required: 'requis',
      tags: 'Tags',
      linksPlaceholder: "Cet utilisateur n'a pas encore ajouté de liens.",
      updateProfile: 'Mettre à jour',
      updateProfileError: 'Erreur lors de la mise à jour du profil.',
      addLinkError: 'Veuillez saisir une adresse valide.'
    }
  }
})

async function initLinks () {
  for (let i = 0; i < linksUrls.value.length; i++) {
    const link: Link = {
      id: 0,
      url: linksUrls.value[i],
      text: cleanUrl(linksUrls.value[i], [ 'https://www.', 'http://www.', 'https://', 'http://' ]),
      linkType: 0,
      linkElement: document.getElementsByClassName('c-profile__link')[0],
      inputElement: document.getElementsByClassName('c-profile__link-edit')[0]
    }

    links.push(link)
  }

  userLinks = await getUserLinks()

  const liElements = document.getElementsByClassName('c-profile__item')
  for (let i = 0; i < links.length; i++) {
    links[i].id = userLinks[i].id
    links[i].linkType = userLinks[i].link_type

    liElements[i].setAttribute('link-id', String(links[i].id))
    liElements[i].setAttribute('link-type', String(links[i].linkType))
  }

  const textareaElements = document.getElementsByClassName('c-profile__link-textarea')
  for (let i = 0; i < links.length; i++) {
    textareaElements[i].setAttribute('index', String(linkIndex))

    linkIndex++
  }
}

initLinks()

onMounted(async () => {
  setProfile()

  // console.log('LINKS:', links)

  links.forEach((link, index) => {
    link.linkElement = document.getElementsByClassName('c-profile__link')[index]
    link.inputElement = document.getElementsByClassName('c-profile__link-edit')[index]
  })

  updateLinksIcons()

  if (isSameUser) {
    addLinkElement = document.querySelector('.c-profile__link-add-item')!
    // toggleElement(addLinkElement, true)
    addLinkElement.addEventListener('click', addLink)

    deleteLinkElements = document.querySelectorAll('.fa-trash-can')
    deleteLinkElements.forEach(trashIcon => {
      trashIcon.addEventListener('click', deleteLink)
    })

    updateLinks()
  }
})

onBeforeUnmount(() => {
  // remove event listeners
  addLinkElement = document.querySelector('.c-profile__link-add-item')!
  addLinkElement.removeEventListener('click', addLink)

  deleteLinkElements = document.querySelectorAll('.fa-trash-can')
  deleteLinkElements.forEach(trashIcon => {
    trashIcon.removeEventListener('click', deleteLink)
  })

  const addedTextarea = document.querySelectorAll('.c-profile__added-textarea')
  addedTextarea.forEach(textarea => {
    textarea.removeEventListener('input', onTextareaInput)
  })

  editIcon.removeEventListener('click', async () => {
    await toggleEditMode()
  })

  cancelIcon.removeEventListener('click', async () => {
    await toggleEditMode()
    removeAddedLinks()
  })

  const confirmButton = <HTMLElement>document.querySelector('.links-confirm__icon')
  confirmButton.addEventListener('click', submitLinksForm)
})

function setProfile () {
  userId = parseInt(props.userId)
  profileId = props.profileId

  userName = props.userName
  profileName = props.profileName

  isSameUser = userName === profileName
}

async function getUserLinks () {
  const allLinks = await getProfileLinks()
  const userLinks: ProfileLink[] = []

  allLinks.forEach(link => {
    if (link.forum_user === userId) {
      userLinks.push(link)
    }
  })

  return userLinks
}

function getMaxId () {
  let maxId = 0

  const items = document.querySelectorAll('.c-profile__item')
  items.forEach(item => {
    const id = parseInt(item.getAttribute('link-id')!)
    maxId = id > maxId ? id : maxId
  })

  return maxId
}

function parseLinks (linksObject: any) {
  const links = []

  const startIndexes = getStrIndex('ForumUserLink: ', linksObject, 15)
  const closeIndexes = getStrIndex('>', linksObject)

  for (let i = 0; i < startIndexes.length; i++) {
    links.push(linksObject.substring(startIndexes[i], closeIndexes[i]))
  }

  return links
}

function getStrIndex (subStr: string, str: string, offset: number = 0) {
  let startIndex = 0
  let index
  const indexes = []

  str = str.toLowerCase()
  subStr = subStr.toLowerCase()

  while ((index = str.indexOf(subStr, startIndex)) > -1) {
    indexes.push(index + offset)
    startIndex = index + subStr.length
  }

  return indexes
}

function cleanUrl (url: string, prefix: Array<string>) {
  for (const pre of prefix) {
    if (url.startsWith(pre)) {
      const rm = pre
      let str = url.slice(rm.length)
      if (str.slice(-1) === '/') str = str.slice(0, -1)
      return str === undefined ? url : str
    }
  }
}

function onTextareaInput (e: Event) {
  const target = e.target as HTMLInputElement

  if (target) {
    // update link text and url when typing
    let selectedLinkIndex: number|string = target.getAttribute('index')!
    selectedLinkIndex = parseInt(selectedLinkIndex)

    if (target.classList.contains('c-profile__added-textarea')) {
      if (links[selectedLinkIndex]) links[selectedLinkIndex].url = target.value
    }

    const linkEl = document.getElementsByClassName('c-profile__link')[selectedLinkIndex]

    linkEl.setAttribute('href', links[selectedLinkIndex].url)

    links[selectedLinkIndex].text = links[selectedLinkIndex].url

    const linkTextEl = document.getElementsByClassName('c-profile__link-text')[selectedLinkIndex]
    linkTextEl.innerHTML = links[selectedLinkIndex].text!
  }
}

function addLink () {
  // create link element
  const linkTextareaEl = document.createElement('textarea')
  linkTextareaEl.classList.add('c-field__input', 'c-profile__link-textarea', 'c-profile__added-textarea')
  linkTextareaEl.cols = 30
  linkTextareaEl.rows = 10
  linkTextareaEl.setAttribute('index', String(linkIndex))
  linkTextareaEl.addEventListener('input', onTextareaInput)

  const linkEditEl = document.createElement('div')
  linkEditEl.classList.add('c-profile__link-edit', 'u-hide')
  linkEditEl.appendChild(linkTextareaEl)

  const linkTextEl = document.createElement('span')
  linkTextEl.classList.add('c-profile__link-text')
  linkTextEl.innerHTML = 'link'

  const linkIconEl = document.createElement('span')
  linkIconEl.classList.add('fa-xl', 'fa-solid', 'fa-link')

  const linkIconItemEl = document.createElement('span')
  linkIconItemEl.classList.add('fa-li')
  linkIconItemEl.appendChild(linkIconEl)
  linkIconItemEl.appendChild(linkTextEl)

  const linkEl = document.createElement('a')
  linkEl.classList.add('c-profile__link', 'u-show')
  linkEl.href = '#'
  linkEl.target = '_blank'
  linkEl.appendChild(linkIconItemEl)

  const linkContentEl = document.createElement('div')
  linkContentEl.classList.add('c-profile__link-content')
  linkContentEl.appendChild(linkEl)

  const linkItemEl = document.createElement('li')
  linkItemEl.classList.add('c-profile__item', 'c-profile__link-added')
  linkItemEl.appendChild(linkContentEl)
  linkItemEl.appendChild(linkEditEl)

  const linkListElement = document.querySelector('.c-profile__items')

  // increment link index
  linkIndex++

  // create new Link instance
  const link: Link = {
    id: 0,
    url: '',
    text: 'link',
    linkType: 0,
    linkElement: linkEl,
    inputElement: linkEditEl
  }

  // add to links list
  links.push(link)

  // add link-id attribute to list item
  linkItemEl.setAttribute('link-id', String(getMaxId() + 1))

  // add link-type attribute to list item
  linkItemEl.setAttribute('link-type', String(0))

  linkListElement?.insertBefore(linkItemEl, addLinkElement)

  // toggle edit mode
  if (!isEditable) {
    editIcon.click()
  } else {
    toggleLinkEdit(link)
  }

  // linkTextareaEl.scrollIntoView({ behavior: 'smooth' })
  linkTextareaEl.focus()
}

function removeAddedLinks () {
  const addedLinkElements = document.querySelectorAll('.c-profile__link-added')

  addedLinkElements.forEach(link => {
    link.remove()
    linkIndex--
  })

  links.forEach((link, index, object) => {
    // 0 is the id of added links
    if (link.id === 0) {
      object.splice(index, 1)
    }
  })
}

function deleteLink (e: Event) {
  const target = e.target as Element
  const li = target.closest('.c-profile__item')
  const id = li?.getAttribute('link-id')!

  links.forEach(link => {
    if (link.id === parseInt(id)) {
      deletedLinks.push(link)
      // object.splice(index, 1)
    }
  })

  const listItems = document.querySelectorAll('.c-profile__item')
  listItems.forEach(item => {
    if (item.getAttribute('link-id') === id) {
      item.classList.add('u-hide')
    }
  })

  linkIndex--
}

function toggleElement (el: Element, visible: boolean) {
  if (visible) {
    el.classList.add('u-show')
    el.classList.remove('u-hide')
  } else {
    el.classList.add('u-hide')
    el.classList.remove('u-show')
  }
}

async function toggleEditMode () {
  isEditable = !isEditable

  if (isEditable) {
    if (profileLinksElement.value) {
      profileLinksElement.value.classList.add('profile-links-editable')
    }

    toggleElement(editIcon, false)
    toggleElement(cancelIcon, true)
    toggleElement(confirmIcon, true)

    toggleElement(addLinkElement, true)
  } else {
    if (profileLinksElement.value) {
      profileLinksElement.value.classList.remove('profile-links-editable')
    }

    toggleElement(editIcon, true)
    toggleElement(cancelIcon, false)
    toggleElement(confirmIcon, false)

    toggleElement(addLinkElement, false)
  }

  links.forEach(async link => {
    await toggleLinkEdit(link)
  })
}

async function toggleLinkEdit (link: Link) {
  if (isEditable) {
    toggleElement(link.linkElement, false)
    toggleElement(link.inputElement, true)
  } else {
    toggleElement(link.linkElement, true)
    toggleElement(link.inputElement, false)
  }
}

function updateLinksIcons () {
  const linkItemElements = document.getElementsByClassName('c-profile__item')

  for (let i = 0; i < linkItemElements.length; i++) {
    const iconElement = <Element>linkItemElements[i].getElementsByClassName('fa-li')[0].firstChild
    const textElement = <Element>linkItemElements[i].getElementsByClassName('c-profile__link-text')[0]

    const socials = [ 'facebook', 'twitter', 'github', 'tumblr', 'instagram', 'youtube', 'vimeo', 'soundcloud', 'bandcamp', 'linkedin' ]

    for (const social of socials) {
      if (iconElement && textElement) {
        checkSocialIcon(iconElement, textElement, social)
      }
    }
  }
}

function checkSocialIcon (iconEl: Element, textEl: Element, socialName: string) {
  if (textEl.innerHTML.includes(socialName)) {
    iconEl.classList.remove('fa-solid', 'fa-link')
    iconEl.classList.add('fa-brands', 'fa-' + socialName)
  } else {
    if (!iconEl.classList.contains('fa-brands')) {
      iconEl.classList.add('fa-solid', 'fa-link')
    }
  }
}

function checkLinkProtocol (text: string) {
  if (text.includes('http')) {
    return text
  } else {
    const linkText = 'http://' + text
    return linkText
  }
}

function setLinkType (url: string) {
  let linkType: number = 1
  const socials = [ 'link', 'facebook', 'twitter', 'github', 'tumblr', 'instagram', 'youtube', 'vimeo' ]

  for (const [ index, social ] of socials.entries()) {
    if (url.includes(social)) {
      linkType = index + 1
      return linkType
    }
  }

  return linkType
}

function updateLinks () {
  if (editIcon) {
    // when clicking on links edit icon
    editIcon.addEventListener('click', async () => {
      await toggleEditMode()
    })

    cancelIcon.addEventListener('click', async () => {
      await toggleEditMode()
      removeAddedLinks()
    })
  }

  // confirm
  const confirmButton = <HTMLElement>document.querySelector('.links-confirm__icon')

  if (confirmButton) {
    confirmButton.addEventListener('click', submitLinksForm)
  }
}

function submitLinksForm () {
  buttonSubmit = <HTMLButtonElement>document.getElementById('button-submit-links')

  if (buttonSubmit) {
    // submit exec
    buttonSubmit.click()

    // remove duplicate added link
    setTimeout(() => {
      const allListElements = document.querySelectorAll('.c-profile__item')

      allListElements.forEach(linkElement => {
        if (linkElement.getAttribute('link-id') === '0') {
          linkElement.remove()
        }
      })
    }, 500)

    // show loading icon
    const checkIcon = confirmIcon.querySelector('.fa-check')
    checkIcon?.classList.remove('fa-solid', 'fa-check')
    checkIcon?.classList.add('fas', 'fa-spinner', 'fa-spin')

    // disable buttons
    confirmIcon.classList.add('c-profile__icon-disabled')
    cancelIcon.classList.add('c-profile__icon-disabled')
  }
}

const submit = useRequest(async () => {
  // SUBMIT
  new Promise(function (resolve) {
    resolve(links)
  }).then(async () => {
    // add new links
    const addedLinksElements = document.querySelectorAll('.c-profile__link-added')
    addedLinksElements.forEach(async el => {
      const text = checkLinkProtocol(el.querySelector('textarea')?.value!)

      const linkType = setLinkType(text!)

      if (text?.length) {
        const data = {
          id: 0,
          forum_user: userId,
          url: text,
          link_type: linkType
        }

        isEdited = true

        await postProfileLink(data)
          .catch((error) => {
            console.error(error)

            const errorAddEl = document.querySelector('.add-link-error')
            errorAddEl?.classList.remove('u-hide')
          })
      }
    })
  }).then(async () => {
    // check if links have been updated
    const updatedLinks: Link[] = []
    links.forEach(link => {
      userLinks.forEach(async userLink => {
        if (link.id === userLink.id) {
          if (link.url !== userLink.url) {
            link.url = checkLinkProtocol(link.url)
            updatedLinks.push(link)
          }
        }
      })
    })

    updatedLinks.forEach(async link => {
      const linkType = setLinkType(link.url)

      const data = {
        id: link.id,
        forum_user: userId,
        url: link.url,
        link_type: linkType
      }

      isEdited = true

      await updateProfileLink(link.id, data)
    })
  }).then(async () => {
    // check if links have been deleted
    if (deletedLinks.length > 0) {
      deletedLinks.forEach(async link => {
        const linkType = setLinkType(link.url)

        const data = {
          id: link.id,
          forum_user: userId,
          url: link.url,
          link_type: linkType
        }

        isEdited = true

        await deleteProfileLink(link.id, data)
      })
    }
  }).catch((error) => {
    console.error(error)
  }).finally(async () => {
    if (isEdited) {
      const currentDateTime = {
        user: profileId,
        date_modified: formatDateTime(new Date())
      }

      await updateDateModified(userId, currentDateTime)

      document.location.reload()
    } else {
      // reset links edit mode
      const checkIcon = confirmIcon.querySelector('.fa-spinner')
      checkIcon?.classList.remove('fas', 'fa-spinner', 'fa-spin')
      checkIcon?.classList.add('fa-solid', 'fa-check')

      toggleElement(editIcon, true)
      toggleElement(cancelIcon, false)
      toggleElement(confirmIcon, false)

      toggleElement(addLinkElement, false)

      links.forEach(link => {
        toggleElement(link.inputElement, false)
        toggleElement(link.linkElement, true)
      })

      isEditable = false
    }
  })
},
{
  error (e) {
    console.log(e)
    // scroll to error message when an error occurs
    if (form.value) {
      // form.value.scrollIntoView({ behavior: 'smooth' })
    }
  }
})

</script>
